import type { IStreamingSegment } from "@lexicanon/shared"
import type { ITranscriptLine } from "../../../lib/types/transcript"

/**
 * Attach `start` / `language` from streaming segments onto transcript lines.
 *
 * The transcript API (`dialogue.txt`) does not carry timestamps or per-line
 * language. Soniox tags both on `streaming_segments`. When the two lists
 * line up 1:1 we zip; otherwise we walk segments in order and match the next
 * line with the same speaker. Lines that don't match are left unchanged —
 * callers must treat `start`/`language` as optional.
 */
export function enrichTranscriptLines(
  lines: readonly ITranscriptLine[],
  segments: readonly IStreamingSegment[] | undefined,
): ITranscriptLine[] {
  if (lines.length === 0 || !segments || segments.length === 0) {
    return lines.map((line) => ({ ...line }))
  }

  if (segments.length === lines.length) {
    return lines.map((line, i) => attachSegment(line, segments[i]))
  }

  let si = 0
  return lines.map((line) => {
    while (si < segments.length) {
      const seg = segments[si]
      si += 1
      if (seg.speaker === line.speaker || seg.text === line.text) {
        return attachSegment(line, seg)
      }
    }
    return { ...line }
  })
}

function attachSegment(line: ITranscriptLine, seg: IStreamingSegment | undefined): ITranscriptLine {
  if (!seg) return { ...line }
  return {
    ...line,
    start: seg.start,
    language: seg.language,
  }
}

export interface ILanguageSwitch {
  code: string
  atSeconds: number
}

/**
 * Primary language + the first later language change, from tagged segments.
 * `fallbackCode` (meeting `analysis_locale`) is used when no segment is tagged.
 */
export function languageTimeline(
  segments: readonly Pick<IStreamingSegment, "start" | "language">[] | undefined,
  fallbackCode?: string | null,
): { primary: string | null; switchTo: ILanguageSwitch | null } {
  const tagged = (segments ?? []).filter((s): s is { start: number; language: string } => {
    return typeof s.language === "string" && s.language.length > 0
  })
  const primary = (tagged[0]?.language ?? fallbackCode ?? null)?.toLowerCase() ?? null
  if (!primary) return { primary: null, switchTo: null }

  for (const seg of tagged) {
    const code = seg.language.toLowerCase()
    if (code !== primary) return { primary, switchTo: { code, atSeconds: seg.start } }
  }
  return { primary, switchTo: null }
}

/** Where a line sits inside a run of the same speaker. */
export type TTranscriptBatchEdge = "only" | "start" | "mid" | "end"

/**
 * A single short reply can sit on one line with the name. Longer turns, and
 * any line that continues a run, stack the words underneath.
 */
export const SHORT_TRANSCRIPT_TURN_CHARS = 90

export function isShortTranscriptTurn(text: string): boolean {
  const trimmed = text.trim()
  return trimmed.length > 0 && trimmed.length <= SHORT_TRANSCRIPT_TURN_CHARS && !trimmed.includes("\n")
}

/** Same resolved speaker, and not a language change. Missing language does not split a run. */
export function sameTranscriptBatch(
  previous: { speaker: string; language?: string },
  next: { speaker: string; language?: string },
): boolean {
  if (previous.speaker !== next.speaker) return false
  const left = previous.language?.toLowerCase()
  const right = next.language?.toLowerCase()
  if (left && right && left !== right) return false
  return true
}

export function transcriptBatchEdge(sameAsPrevious: boolean, sameAsNext: boolean): TTranscriptBatchEdge {
  if (sameAsPrevious && sameAsNext) return "mid"
  if (sameAsPrevious) return "end"
  if (sameAsNext) return "start"
  return "only"
}

/** Edge of the line at `index`, compared on resolved speaker labels. */
export function transcriptLineEdge(
  lines: readonly { speaker: string; language?: string }[],
  index: number,
  resolveSpeaker: (speaker: string) => string,
): TTranscriptBatchEdge {
  const at = (i: number): { speaker: string; language?: string } | undefined => {
    const line = lines[i]
    if (!line) return undefined
    return { speaker: resolveSpeaker(line.speaker), language: line.language }
  }
  const line = at(index)
  if (!line) return "only"
  const previous = at(index - 1)
  const next = at(index + 1)
  return transcriptBatchEdge(
    previous ? sameTranscriptBatch(previous, line) : false,
    next ? sameTranscriptBatch(line, next) : false,
  )
}

/** Elapsed seconds as zero-padded `hh:mm:ss`. */
export function formatElapsedHms(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00:00"
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
}

/**
 * Offset from the start of the meeting. Under an hour: `00:12`. With hours: `1:04:05`.
 * The same shape the speaker panel uses for "first spoke", extended past 59 minutes.
 */
export function formatMeetingOffset(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00"
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = m.toString().padStart(2, "0")
  const ss = s.toString().padStart(2, "0")
  if (h > 0) return `${h}:${mm}:${ss}`
  return `${mm}:${ss}`
}
